import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { MaterialIcon } from '@/components/MaterialIcon';
import { sendLiveViewCommand, sendFocusDriveCommand } from '@/services/viewService';
import type { View } from '@/types';

const { Text } = Typography;

const CONNECTION_TIMEOUT_MS = 15_000;
const STALE_FRAME_MS = 15_000;

interface LiveViewFocusOverlayProps {
  view: View;
  focusMode: string | null | undefined;
  connected: boolean;
  onClose: () => void;
}

const STEP_BUTTONS: { label: string; direction: 'near' | 'far'; steps: number }[] = [
  { label: '<<<', direction: 'near', steps: 15 },
  { label: '<<', direction: 'near', steps: 5 },
  { label: '<', direction: 'near', steps: 3 },
  { label: '>', direction: 'far', steps: 3 },
  { label: '>>', direction: 'far', steps: 5 },
  { label: '>>>', direction: 'far', steps: 15 },
];

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

export function LiveViewFocusOverlay({ view, focusMode, connected, onClose }: LiveViewFocusOverlayProps) {
  const [lvFrame, setLvFrame] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [pendingCmd, setPendingCmd] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const lastPanRef = useRef({ x: 0, y: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const lastFrameTimeRef = useRef(0);
  const viewIds = useRef({ userId: view.userId, projectId: view.projectId, viewId: view.viewId });

  const isMF = focusMode === 'MF';

  useEffect(() => {
    const { userId, projectId, viewId } = viewIds.current;

    sendLiveViewCommand(userId, projectId, viewId, 'start')
      .catch((err) => console.error('[LiveViewFocus] Failed to start LV', err));

    const handleBeforeUnload = () => {
      sendLiveViewCommand(userId, projectId, viewId, 'stop').catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendLiveViewCommand(userId, projectId, viewId, 'stop')
        .catch((err) => console.error('[LiveViewFocus] Failed to stop LV', err));
    };
  }, []);

  useEffect(() => {
    if (!connecting) return;
    const timer = setTimeout(() => {
      if (connecting) {
        setConnecting(false);
        setError('Live view connection timed out. Camera may be busy or disconnected.');
      }
    }, CONNECTION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [connecting]);

  useEffect(() => {
    if (!lvFrame) return;
    const interval = setInterval(() => {
      if (lastFrameTimeRef.current > 0 && Date.now() - lastFrameTimeRef.current > STALE_FRAME_MS) {
        setStale(true);
      }
      if (pendingCmdTimeRef.current > 0 && Date.now() - pendingCmdTimeRef.current > 15_000) {
        setPendingCmd(false);
        pendingCmdTimeRef.current = 0;
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [lvFrame]);

  useEffect(() => {
    const viewRef = doc(db, `users/${view.userId}/projects/${view.projectId}/views/${view.viewId}`);

    const unsubscribe = onSnapshot(viewRef, (snapshot) => {
      const data = snapshot.data();
      const frame = data?.androidStatus?.lvFrame;
      const lvError = data?.androidStatus?.lvError;

      if (lvError) {
        setError(lvError);
        setConnecting(false);
        return;
      }

      if (frame) {
        setLvFrame(frame);
        setConnecting(false);
        setError(null);
        setStale(false);
        lastFrameTimeRef.current = Date.now();
        if (pendingCmdTimeRef.current > 0) {
          setPendingCmd(false);
          pendingCmdTimeRef.current = 0;
        }
      }
    });

    return () => unsubscribe();
  }, [view.userId, view.projectId, view.viewId]);

  useEffect(() => {
    if (!connected && lvFrame) {
      onClose();
    }
  }, [connected, lvFrame, onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const pendingCmdTimeRef = useRef(0);

  const handleStep = useCallback((direction: 'near' | 'far', steps: number) => {
    if (!isMF || pendingCmd) return;
    setPendingCmd(true);
    pendingCmdTimeRef.current = Date.now();
    sendFocusDriveCommand(view.userId, view.projectId, view.viewId, direction, steps)
      .catch((err) => {
        console.error('[LiveViewFocus] Focus step failed', err);
        setPendingCmd(false);
        pendingCmdTimeRef.current = 0;
      });
  }, [isMF, pendingCmd, view.userId, view.projectId, view.viewId]);

  const handleRetry = () => {
    setError(null);
    setConnecting(true);
    setStale(false);
    lastFrameTimeRef.current = 0;
    sendLiveViewCommand(view.userId, view.projectId, view.viewId, 'start')
      .catch((err) => console.error('[LiveViewFocus] Failed to restart LV', err));
  };

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setZoom((z) => {
        const next = z - e.deltaY * 0.002;
        const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        if (clamped === MIN_ZOOM) setPan({ x: 0, y: 0 });
        return clamped;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (zoom <= MIN_ZOOM) return;
    isPanningRef.current = true;
    lastPanRef.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [zoom]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isPanningRef.current) return;
    const dx = e.clientX - lastPanRef.current.x;
    const dy = e.clientY - lastPanRef.current.y;
    lastPanRef.current = { x: e.clientX, y: e.clientY };
    setPan((p) => ({ x: p.x + dx, y: p.y + dy }));
  }, []);

  const handlePointerUp = useCallback(() => {
    isPanningRef.current = false;
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (zoom > MIN_ZOOM) {
      setZoom(MIN_ZOOM);
      setPan({ x: 0, y: 0 });
    } else {
      setZoom(2.5);
    }
  }, [zoom]);

  const stepButtonStyle: React.CSSProperties = {
    minWidth: 48,
    height: 44,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.12)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    padding: '0 10px',
  };

  return (
    <div ref={overlayRef} style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: '#000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      {/* Close button */}
      <Button
        type="text"
        icon={<CloseOutlined />}
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: '#fff',
          fontSize: 20,
          zIndex: 1002,
        }}
      />

      {/* Zoomable image layer — only the img gets transform */}
      <div
        ref={imgContainerRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: zoom > MIN_ZOOM ? 'grab' : 'default',
          touchAction: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={handleDoubleClick}
      >
        {lvFrame && (
          <img
            src={`data:image/jpeg;base64,${lvFrame}`}
            alt="Live View"
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transformOrigin: 'center center',
              transition: isPanningRef.current ? 'none' : 'transform 0.1s ease-out',
            }}
          />
        )}
      </div>

      {/* Fixed status messages (not zoomable) */}
      {error && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              <MaterialIcon name="error_outline" size={48} style={{ color: 'rgba(255,100,100,0.7)' }} />
            </div>
            <Text style={{ color: 'rgba(255,100,100,0.9)', display: 'block', marginBottom: 16 }}>
              {error}
            </Text>
            <Button type="primary" ghost size="small" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        </div>
      )}
      {!error && connecting && !lvFrame && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 16 }}>
              <MaterialIcon name="videocam" size={48} style={{ color: 'rgba(255,255,255,0.4)' }} />
            </div>
            <Text style={{ color: 'rgba(255,255,255,0.6)' }}>
              Starting live view...
            </Text>
          </div>
        </div>
      )}

      {/* Zoom indicator */}
      {zoom > MIN_ZOOM && lvFrame && (
        <div style={{
          position: 'absolute',
          top: 16,
          right: 50,
          zIndex: 1001,
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          borderRadius: 6,
          padding: '4px 8px',
        }}>
          <Text style={{ color: '#fff', fontSize: 12 }}>{zoom.toFixed(1)}x</Text>
        </div>
      )}

      {/* Stale frame warning */}
      {stale && lvFrame && (
        <div style={{
          position: 'absolute',
          top: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255, 165, 0, 0.2)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 165, 0, 0.4)',
            borderRadius: 8,
            padding: '6px 14px',
          }}>
            <MaterialIcon name="wifi_off" size={16} style={{ color: '#ffa500' }} />
            <Text style={{ color: '#ffa500', fontSize: 12 }}>
              Feed interrupted — last frame may be stale
            </Text>
          </div>
        </div>
      )}

      {/* Focus step buttons */}
      <div style={{
        position: 'absolute',
        bottom: 48,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        zIndex: 1001,
      }}>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginRight: 4 }}>Near</span>
        {STEP_BUTTONS.map((btn) => (
          <Button
            key={btn.label}
            type="text"
            style={{
              ...stepButtonStyle,
              opacity: isMF && !pendingCmd ? 1 : 0.3,
              cursor: isMF && !pendingCmd ? 'pointer' : 'not-allowed',
            }}
            onClick={() => handleStep(btn.direction, btn.steps)}
            disabled={!isMF || pendingCmd}
          >
            {btn.label}
          </Button>
        ))}
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginLeft: 4 }}>Far</span>
      </div>

      {/* Focus experimental disclaimer */}
      {isMF && (
        <div style={{
          position: 'absolute',
          bottom: 20,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1001,
        }}>
          <Text style={{ color: 'rgba(255, 165, 0, 0.6)', fontSize: 11 }}>
            ⚠️ Experimental — focus drive may cause camera disconnection requiring a manual power cycle
          </Text>
        </div>
      )}

      {/* AF mode warning */}
      {!isMF && (
        <div style={{
          position: 'absolute',
          bottom: 108,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(255, 165, 0, 0.2)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 165, 0, 0.4)',
            borderRadius: 8,
            padding: '8px 16px',
          }}>
            <MaterialIcon name="info" size={18} style={{ color: '#ffa500' }} />
            <Text style={{ color: '#ffa500', fontSize: 13 }}>
              Switch to MF on camera to adjust focus
            </Text>
          </div>
        </div>
      )}
    </div>
  );
}
